import { createSelector } from '@reduxjs/toolkit'
import isNull from 'lodash/isNull'
import { call, delay, put, select } from 'redux-saga/effects'

import { isActiveDrawableHeader } from '../utils/isActiveDrawableHeader'
import { updateLabelPositionAndText } from '../utils/updateLabelPositionAndText'
import { calculateAreaMeasurementsForMaterial, calculateLFMeasurementOfMaterial } from './createDrawableLocation'
import drawShapeByType from './drawShapeByType'
import { RedrawShapeStoreState, selectRedrawState } from './handleUpdateOpeningGroupsSuccess'
import { redrawFlagsOnUpdate } from './redrawFlagsOnUpdate'
import { resetDrawableHighlightState } from './resetDrawableHighlightState'
import { updatedDrawableMeasurements, updateOpeningGroupsSuccess } from '../../../actions/drawable'
import { UpdateOpeningGroupApiResponse } from '../../../api/api-helper'
import { updateOpeningLocationCoordinates } from '../../../api/projects-api'
import { updateJoistLines } from '../../../api/takeoff-api'
import { Coordinate } from '../../../models/activeDrawable'
import { GeneralDrawableSettings } from '../../../models/activeDrawableSettings'
import { ActiveFloor } from '../../../models/activeFloor'
import { DRAWABLE_TYPES, DRAWING_TYPES } from '../../../shared/constants/drawable-types'
import { DEFAULT_SCALE_FACTOR } from '../../../shared/constants/scales'
import { RootState } from '../../../stores'
import { calculateAreaFromCoords } from '../../../utils/calculations/areaCalculation/calculateAreaFromCoords'
import {
    applyScaleFactorToPathLength,
    convertScaleFactorLabelEnumToDecimal,
    DPI_72,
} from '../../../utils/calculations/scaleConversion/scaleConversion'
import { defineLocationRegion } from '../../../utils/coordinates/defineLocationRegion'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Label, Select, Workspace } from '../../lib/toolBoxes/2D/tools'
import {
    getCurrentDrawableLocations,
    resetUpdatedCoordinates,
    updateDrawableLocationCoordinates,
    updateSelectedCoordinates,
} from '../../slices/2D'
import { updateToolbarMessage } from '../../slices/common'
import { selectDrawableActiveFloor } from '../../slices/documents'
import { setFormPendingStatus } from '../../slices/forms'
import {
    ActiveGeometryGroup,
    selectActiveGeometryGroup,
    updateGeometricLocationAdditionalData,
} from '../../slices/geometry'
import { selectProjectId } from '../../slices/project'
import {
    IMUP2DCoordinatesToUpdate,
    IMUP2DDrawableCutout,
    IMUP2DDrawableLocation,
    MeasurementsToUpdate,
    SAVING_CHANGES_ERROR_MESSAGE,
    SAVING_MESSAGE,
    TOOLBAR_MESSAGE_TIMER,
} from '../../types'

export interface UpdateCoordinatesState {
    activeDrawableId: number | null
    activeDrawableGroup: ActiveGeometryGroup | null
    activeOpeningLocationId: number | null
    drawableLocations: IMUP2DDrawableLocation[]
    selectedItemId: number | null
}

export interface UpdateDrawableMeasurements {
    activeDrawableId: number
    groupMeasurements: MeasurementsToUpdate
    openingMeasurements: MeasurementsToUpdate
    coordinates: Coordinate[]
    cutouts: IMUP2DDrawableCutout[] | undefined
    updatedLocationID: number
}

export const selectUpdateCoordinatesState = createSelector(
    (state: RootState): UpdateCoordinatesState => ({
        drawableLocations: getCurrentDrawableLocations(state.IMUP),
        activeDrawableGroup: selectActiveGeometryGroup(state),
        activeDrawableId: state.IMUP['2D'].activeDrawableId,
        activeOpeningLocationId: state.IMUP['2D'].activeOpeningLocationId,
        selectedItemId: state.IMUP['2D'].selectedItem,
    }),
    (state: UpdateCoordinatesState): UpdateCoordinatesState => state
)

export const prepareMeasurements = (
    measurements: MeasurementsToUpdate,
    typeAndSettings: { type?: DRAWABLE_TYPES; settings?: GeneralDrawableSettings }
): MeasurementsToUpdate => {
    const rawMeasurements = { ...measurements }

    if (Boolean(rawMeasurements.area) && !isNull(rawMeasurements.area)) {
        const newArea = calculateAreaMeasurementsForMaterial(rawMeasurements.area || 0, typeAndSettings)

        return {
            ...rawMeasurements,
            area: rawMeasurements.area,
            quantity: newArea,
        }
    } else if (Boolean(rawMeasurements.linear_total) && !isNull(rawMeasurements.linear_total)) {
        const newLF = calculateLFMeasurementOfMaterial(rawMeasurements.linear_total, typeAndSettings)

        return {
            ...rawMeasurements,
            linear_total: rawMeasurements.linear_total,
            quantity: newLF,
        }
    }

    return rawMeasurements
}

export function* clearToolbarMessageAndClearCoordinateState() {
    // Clear the state
    yield put(resetUpdatedCoordinates())
    yield delay(TOOLBAR_MESSAGE_TIMER)
    yield put(updateToolbarMessage(null))
}

export function* updateJoistDataForGroup(projectId: number, groupId: number, openingIds?: number[]) {
    yield put(yield call(updateJoistLines, projectId, groupId, openingIds))
}

const PATH_SHAPE_TYPES = [DRAWING_TYPES.AREA, DRAWING_TYPES.SECTION, DRAWING_TYPES.RADIUS]

/**
 * Finds the paper item that carries the drawable's geometry:
 * - a plain path or a CompoundPath (area with cutouts, its children have no shapeType) is the path itself
 * - a group child with a shapeType is the path
 * - a label group (e.g. header beam: path + label) keeps shapeType on the group, its path is the first child
 */
export const findDrawablePath = (item: paper.Item): paper.Item | undefined => {
    if (!item.children?.length || item.className === 'CompoundPath') return item

    const childWithShapeType = item.children.find((child) => PATH_SHAPE_TYPES.includes(child.data.shapeType))

    if (childWithShapeType) return childWithShapeType

    return item.data?.shapeType ? item.children.find((child) => child.className === 'Path') : undefined
}

export function* generateUpdateDrawableCoordinatesEndpointData(
    coordinates: IMUP2DCoordinatesToUpdate,
    item: paper.Item,
    currentLocation: IMUP2DDrawableLocation | undefined,
    activeFloor: ActiveFloor,
    workspaceTool: Workspace,
    groupTypesAndSettings: { type?: DRAWABLE_TYPES; settings?: GeneralDrawableSettings }
) {
    const path = findDrawablePath(item)

    if (!path) throw new Error('Path not found in item children')
    const castPath = path as paper.Path
    const regionId = defineLocationRegion(castPath, activeFloor.document_chunk.regions, workspaceTool)
    const projectId = yield select(selectProjectId)

    let newQty = 0

    if (currentLocation && currentLocation.region_id !== regionId) {
        const scaleFactor = regionId
            ? activeFloor.document_chunk.regions.find((r) => r.id === regionId)?.scale
            : activeFloor.scale_factor

        const scale = convertScaleFactorLabelEnumToDecimal(scaleFactor ?? DEFAULT_SCALE_FACTOR)

        // Update item scale factor
        item.data.scale = scaleFactor

        switch (item.data.shapeType) {
            case DRAWING_TYPES.AREA: {
                // Computed from the persisted coordinates, not paper.js path.area, to match the backend's recompute
                const convertAreaCoordsToSquareFeet = (areaCoords: Coordinate[]): number =>
                    calculateAreaFromCoords({
                        coords: areaCoords.flatMap((coord) => coord),
                        scaleFactor: Number(scale),
                        dpi: activeFloor.document_chunk.dpi ?? DPI_72,
                        xCalibrationFactor: activeFloor.document_chunk.calibration_factor_x,
                        yCalibrationFactor: activeFloor.document_chunk.calibration_factor_y,
                        pdfScale: activeFloor.document_chunk.pdf_scale ?? 1,
                    })

                newQty =
                    convertAreaCoordsToSquareFeet(coordinates.coordinates) -
                    (coordinates.cutouts ?? []).reduce(
                        (total, cutout) => total + convertAreaCoordsToSquareFeet(cutout.coordinates),
                        0
                    )
                break
            }
            case DRAWING_TYPES.SECTION:
            case DRAWING_TYPES.RADIUS:
                newQty = applyScaleFactorToPathLength({
                    coordinates: coordinates.coordinates,
                    pxValue: castPath.length,
                    scaleFactor: Number(scale),
                    dpi: activeFloor.document_chunk.dpi ?? DPI_72,
                    xCalibrationFactor: activeFloor.document_chunk.calibration_factor_x,
                    yCalibrationFactor: activeFloor.document_chunk.calibration_factor_y,
                    pdfScale: activeFloor.document_chunk.pdf_scale ?? 1,
                })
                break
            default:
                break
        }
    }

    const measurements = {
        ...coordinates.measurements,
        linear_total: newQty ? Math.abs(newQty) : coordinates.measurements.linear_total,
        area: newQty ? Math.abs(newQty) : coordinates.measurements.area,
    }

    const openingId = item.data.drawable_id

    yield call(redrawFlagsOnUpdate, openingId, coordinates)

    return {
        coordinatesData: {
            ...coordinates,
            measurements: prepareMeasurements(measurements, {
                type: groupTypesAndSettings.type,
                settings: groupTypesAndSettings.settings,
            }),
        },
        projectId,
        regionId,
    }
}

type SavedItemSelection = Pick<
    UpdateCoordinatesState,
    'activeDrawableId' | 'activeOpeningLocationId' | 'selectedItemId'
>

function* resolveSavedItemSelection(
    paperItemId: number | undefined,
    activeSelection: SavedItemSelection
): Generator<unknown, SavedItemSelection, any> {
    if (!paperItemId) return activeSelection

    const paperManager: PaperManager = yield call(managers.get2DManager)
    const workspaceTool: Workspace = yield call(paperManager.getTool, Workspace.NAME)
    const savedItem: paper.Item | null = yield call(workspaceTool.getItemWithPaperId, paperItemId)

    if (!savedItem) return activeSelection

    return {
        activeDrawableId: savedItem.data.drawable_id,
        activeOpeningLocationId: savedItem.data.opening_location_id,
        selectedItemId: savedItem.id,
    }
}

export function* updateActiveDrawableCoordinates(action: ReturnType<typeof updateSelectedCoordinates>) {
    // get coordinates state from redux store
    const { drawableLocations, activeDrawableGroup, ...selection }: UpdateCoordinatesState = yield select(
        selectUpdateCoordinatesState
    )

    yield put(updateToolbarMessage(SAVING_MESSAGE))

    const activeFloor: ActiveFloor = yield select(selectDrawableActiveFloor)

    const { activeDrawableId, activeOpeningLocationId, selectedItemId } = yield* resolveSavedItemSelection(
        action.payload.paperItemId,
        selection
    )

    const coordinates: IMUP2DCoordinatesToUpdate = {
        coordinates: action.payload.coordinates,
        cutouts: action.payload.cutouts,
        measurements: action.payload.measurements,
        region_id: action.payload.region_id,
    }

    try {
        // Call API to update opening
        if (((activeDrawableId && activeOpeningLocationId) || selectedItemId) && coordinates && activeDrawableGroup) {
            const paperManager = yield call(managers.get2DManager)
            const [workspaceTool, labelTool]: [Workspace, Label] = yield call(paperManager.getTools, [
                Workspace.NAME,
                Label.NAME,
            ])

            let item: paper.Item | null = null

            if (activeOpeningLocationId) {
                item = yield call(workspaceTool.getItemWithOpeningLocationId, activeOpeningLocationId)
            }
            if (!item && activeDrawableId) {
                item = yield call(workspaceTool.getItemWithDrawableId, activeDrawableId)
            }
            if (!item && selectedItemId) {
                item = yield call(workspaceTool.getItemWithPaperId, selectedItemId)
            }
            if (!item) {
                throw new Error('Item being edited was not found')
            }

            // If region_id has changed, measurements have to be updated considering new scale
            const currentLocation = drawableLocations.find((l) => l.opening_location_id === activeOpeningLocationId)

            const {
                coordinatesData,
                projectId,
                regionId,
            }: { coordinatesData: IMUP2DCoordinatesToUpdate; projectId: number; regionId: number | null } =
                yield generateUpdateDrawableCoordinatesEndpointData(
                    coordinates,
                    item,
                    currentLocation,
                    activeFloor,
                    workspaceTool,
                    activeDrawableGroup
                )

            const openingId = activeDrawableId || item.data.drawable_id

            if (isActiveDrawableHeader(activeDrawableGroup)) {
                yield put(setFormPendingStatus(true))
            }

            const response: UpdateOpeningGroupApiResponse = yield call(
                updateOpeningLocationCoordinates,
                projectId,
                openingId,
                activeOpeningLocationId || item.data.opening_location_id,
                coordinatesData,
                regionId
            )

            // After API success, update drawableLocations 2D slice and
            // update opening & opening group settings (quantity/linear total)
            const openingLocationId = activeOpeningLocationId || item.data.opening_location_id
            const opening = response.newGroup.openings.find((o) =>
                o.opening_locations.some((location) => location.id === openingLocationId)
            )

            const openingLocation = opening?.opening_locations.find((l) => l.id === openingLocationId)

            // Moving between regions can merge groups. Keep selection metadata in sync because
            // UPDATE_OPENING_GROUPS_SUCCESS below skips redrawing this canvas item.
            if (opening) {
                item.data.opening_group_id = response.newGroup.id
                item.data.drawable_id = opening.id
            }

            if (openingLocation?.additional_data && opening?.additional_data) {
                yield put(
                    updateGeometricLocationAdditionalData({
                        locationAdditionalData: openingLocation.additional_data,
                        id: openingLocation.id,
                        openingAdditionalData: opening.additional_data,
                    })
                )
            }

            const newCutouts = coordinates.cutouts

            // update the quantity & linear total inside the activeDrawableGroup and drawable groups
            const groupMeasurements = {
                quantity: response.newGroup.settings.quantity,
                linear_total: response.newGroup.settings.linear_total,
            }

            const openingMeasurements = {
                area: opening?.settings.area,
                quantity: opening?.settings.quantity ?? null,
                linear_total: opening?.settings.linear_total ?? null,
            }

            yield call(
                updateLabelPositionAndText,
                labelTool,
                item,
                activeDrawableGroup.type,
                activeDrawableGroup.settings,
                openingMeasurements.quantity ?? 0
            )

            // Update coordinates BEFORE UPDATE_OPENING_GROUPS_SUCCESS (saga reads from Redux state)
            yield put(
                updateDrawableLocationCoordinates({
                    opening_location_id: openingLocationId,
                    opening_id: opening?.id,
                    opening_group_id: opening ? response.newGroup.id : undefined,
                    settings: opening?.settings,
                    coordinates: coordinates.coordinates,
                    linear_total: openingMeasurements.linear_total,
                    quantity: openingMeasurements.quantity,
                    cutouts: newCutouts,
                    region_id: regionId,
                })
            )

            yield put(
                updatedDrawableMeasurements({
                    activeDrawableId: openingId,
                    groupMeasurements,
                    openingMeasurements,
                    cutouts: newCutouts,
                    coordinates: coordinates.coordinates,
                    updatedLocationID: activeOpeningLocationId || item.data.opening_location_id,
                })
            )

            // Dispatch UPDATE_OPENING_GROUPS_SUCCESS to update geometry slice with new group info
            // This handles location changes (ex. FLOOR_AREA, doors moving between regions - skip redraw)
            yield put(updateOpeningGroupsSuccess(response, true))

            yield put(resetUpdatedCoordinates())
            yield put(setFormPendingStatus(false))
            yield put(updateToolbarMessage(null))

            if (item.strokeColor || item.fillColor) yield call(resetDrawableHighlightState, item)

            if (item.strokeWidth) item.strokeWidth = item.data.originalStrokeWidth
        } else {
            throw new Error('Item being edited was not found')
        }
    } catch (error) {
        console.error(error)
        try {
            yield put(updateToolbarMessage(SAVING_CHANGES_ERROR_MESSAGE))

            /* Redraw item to the original state */
            // 1. Remove drawable from workspace
            const manager: PaperManager = yield call(managers.get2DManager)

            const [workspaceTool, selectTool]: [Workspace, Select] = yield call(manager.getTools, [
                Workspace.NAME,
                Select.NAME,
            ])

            let item: paper.Item | null = null

            if (activeOpeningLocationId) {
                item = yield call(workspaceTool.getItemWithOpeningLocationId, activeOpeningLocationId)
            }
            if (!item && activeDrawableId) {
                item = yield call(workspaceTool.getItemWithDrawableId, activeDrawableId)
            }
            if (!item && selectedItemId) {
                item = yield call(workspaceTool.getItemWithPaperId, selectedItemId)
            }

            // 2. Remove vertices & disable select mode
            yield call(selectTool.exitSelectMode)

            if (item) {
                yield call([item, 'remove'])
            }

            // 3. Redraw drawable in the original state
            const { areaOpacity, lineOpacity }: RedrawShapeStoreState = yield select(selectRedrawState)

            const location: IMUP2DDrawableLocation | undefined = drawableLocations.find(
                (l) => l.opening_location_id === (activeOpeningLocationId || item?.data.opening_location_id)
            )

            if (!location) {
                throw Error('Location not found')
            }

            const regionPaths: paper.Path[] = yield call(workspaceTool.getAllRegionItems)

            yield call(drawShapeByType, location, areaOpacity, lineOpacity, regionPaths)

            yield clearToolbarMessageAndClearCoordinateState()
        } catch (e) {
            console.error(e)
            yield clearToolbarMessageAndClearCoordinateState()
        }
    }
}
